import { CSSProperties, PropsWithChildren } from "react";
import { Dialog as ArkDialog, Portal, PortalProps } from "@ark-ui/react";

import {
  aromaDialog,
  AromaDialogVariantProps,
} from "@zenchef/styled-system/recipes";
import { token } from "@zenchef/styled-system/tokens";
import Footer, { FooterExposedProps } from "../Footer";
import { HeaderExposedProps } from "../Header";
import useWindowSize from "../../../utils/useWindowSize";

export interface DialogProps
  extends AromaDialogVariantProps,
    PropsWithChildren,
    HeaderExposedProps,
    FooterExposedProps,
    Omit<ArkDialog.RootProps, "id" | "ids"> {
  trigger?: React.ReactNode;
  portal?: PortalProps;
  zIndex?: number;
  isFullWidthBtn?: boolean;
  btnClassName?: string;
}

const Dialog = ({
  lazyMount = true,
  trapFocus = false,
  unmountOnExit = true,
  children,
  trigger,
  zIndex = 1001,
  title,
  portal,
  primaryAction,
  secondaryAction,
  primaryHierarchy = "primary-bold",
  secondaryHierarchy = "secondary-subtler",
  isFullWidthBtn,
  btnClassName,
  ...props
}: DialogProps) => {
  const [aromaDialogVariantProps, restProps] =
    aromaDialog.splitVariantProps(props);
  const classes = aromaDialog(aromaDialogVariantProps);
  const isSmall = aromaDialogVariantProps.size === "small";
  const { width: windowWidth } = useWindowSize();
  const isMobileScreen = windowWidth <= parseInt(token("breakpoints.xs"), 10);
  const isMediumScreen = windowWidth >= parseInt(token("breakpoints.md"), 10);

  const cssVars = {
    "--dialog-z-index": zIndex,
    "--backdrop-z-index": String(Number(zIndex) - 1),
    "--dialog-width": isMobileScreen ? "100%" : "360px",
    "--dialog-max-height": `calc(100% - ${isMobileScreen ? "48" : "80"}px)`,
    "--dialog-gap": isMediumScreen ? "40px" : "32px",
  } as CSSProperties;

  return (
    <ArkDialog.Root
      closeOnInteractOutside={false}
      closeOnEscape={false}
      defaultOpen={false}
      lazyMount={lazyMount}
      trapFocus={trapFocus}
      unmountOnExit={unmountOnExit}
      {...restProps}
    >
      {trigger && <ArkDialog.Trigger asChild>{trigger}</ArkDialog.Trigger>}
      <Portal {...portal}>
        <ArkDialog.Backdrop className={classes.backdrop} style={cssVars} />
        <ArkDialog.Positioner className={classes.positioner} style={cssVars}>
          <ArkDialog.Content className={classes.content} style={cssVars}>
            <ArkDialog.Title>{title}</ArkDialog.Title>
            <ArkDialog.Description>{children}</ArkDialog.Description>
            <Footer
              classes={classes}
              primaryAction={primaryAction}
              secondaryAction={secondaryAction}
              fullWidth={isFullWidthBtn ?? isSmall}
              primaryHierarchy={primaryHierarchy}
              secondaryHierarchy={secondaryHierarchy}
              className={btnClassName}
            />
          </ArkDialog.Content>
        </ArkDialog.Positioner>
      </Portal>
    </ArkDialog.Root>
  );
};

export default Dialog;
